import { useCallback, useEffect, useRef, useState } from "react";
import {
  type ServerWorkspace,
  type ServerWorkspaceConfig,
  type WorkspaceConfigWrite,
  type WorkspaceWrite,
  api,
} from "../lib/api.ts";

interface Props {
  syncKey: string;
}

function fmtDate(ts: number) {
  return new Date(ts * 1000).toLocaleString();
}

function newId() {
  return crypto.randomUUID();
}

// ── Config editor for one workspace ──────────────────────────────────────────

interface ConfigEditorProps {
  workspace: ServerWorkspace;
  configs: ServerWorkspaceConfig[];
  syncKey: string;
  onReload: () => void;
}

function ConfigEditor({ workspace, configs, syncKey, onReload }: ConfigEditorProps) {
  const mine = configs.filter((c) => c.workspaceId === workspace.id && !c.deletedAt);
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Record<string, string>>({});

  async function addRow() {
    const k = newKey.trim();
    const v = newVal.trim();
    if (!k) return;
    setSaving(true);
    await api.workspaces
      .saveConfigs(syncKey, [{ workspaceId: workspace.id, key: k, value: v }])
      .catch(() => {});
    setNewKey("");
    setNewVal("");
    setSaving(false);
    onReload();
  }

  async function updateRow(cfg: ServerWorkspaceConfig, value: string) {
    await api.workspaces
      .saveConfigs(syncKey, [{ workspaceId: workspace.id, key: cfg.key, value }])
      .catch(() => {});
    setEditing((prev) => { const next = { ...prev }; delete next[cfg.id]; return next; });
    onReload();
  }

  async function deleteRow(cfg: ServerWorkspaceConfig) {
    await api.workspaces
      .saveConfigs(syncKey, [
        { workspaceId: workspace.id, key: cfg.key, value: cfg.value, deletedAt: Math.floor(Date.now() / 1000) },
      ])
      .catch(() => {});
    onReload();
  }

  return (
    <div className="settings-section" style={{ marginTop: 8 }}>
      <h3 style={{ fontSize: 13, marginBottom: 8, color: "var(--color-text-muted, #aaa)" }}>
        Config keys
      </h3>
      {mine.length === 0 ? (
        <p className="empty" style={{ marginBottom: 8 }}>No config keys yet.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8, fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #333" }}>
              <th style={{ padding: "4px 8px", fontWeight: 600 }}>Key</th>
              <th style={{ padding: "4px 8px", fontWeight: 600 }}>Value</th>
              <th style={{ padding: "4px 8px", fontWeight: 600, width: 120 }}>Modified</th>
              <th style={{ padding: "4px 8px", width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {mine.map((cfg) => (
              <tr key={cfg.id} style={{ borderBottom: "1px solid #222" }}>
                <td style={{ padding: "4px 8px", fontFamily: "monospace" }}>{cfg.key}</td>
                <td style={{ padding: "4px 8px" }}>
                  {editing[cfg.id] !== undefined ? (
                    <input
                      value={editing[cfg.id]}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [cfg.id]: e.target.value }))}
                      onBlur={() => updateRow(cfg, editing[cfg.id] ?? cfg.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") updateRow(cfg, editing[cfg.id] ?? cfg.value); if (e.key === "Escape") setEditing((prev) => { const n = { ...prev }; delete n[cfg.id]; return n; }); }}
                      autoFocus
                      style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditing((prev) => ({ ...prev, [cfg.id]: cfg.value }))}
                      style={{ background: "none", border: "none", textAlign: "left", fontFamily: "monospace", fontSize: 12, cursor: "pointer", color: "inherit", padding: 0 }}
                    >
                      {cfg.value || <em style={{ color: "#666" }}>(empty)</em>}
                    </button>
                  )}
                </td>
                <td style={{ padding: "4px 8px", fontSize: 11, color: "#666" }}>{fmtDate(cfg.updatedAt)}</td>
                <td style={{ padding: "4px 8px" }}>
                  <button type="button" onClick={() => deleteRow(cfg)} className="clip-icon-btn" title="Delete" aria-label="Delete config key">🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          placeholder="key"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          style={{ flex: 1, fontFamily: "monospace", fontSize: 12 }}
        />
        <input
          placeholder="value"
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
          style={{ flex: 2, fontFamily: "monospace", fontSize: 12 }}
        />
        <button type="button" onClick={addRow} disabled={saving || !newKey.trim()}>
          {saving ? "…" : "Add"}
        </button>
      </div>
    </div>
  );
}

// ── Workspace row ─────────────────────────────────────────────────────────────

interface WorkspaceRowProps {
  ws: ServerWorkspace;
  configs: ServerWorkspaceConfig[];
  syncKey: string;
  onReload: () => void;
  activeId: string | null;
  onSetActive: (id: string | null) => void;
}

function WorkspaceRow({ ws, configs, syncKey, onReload, activeId, onSetActive }: WorkspaceRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(ws.name);
  const [icon, setIcon] = useState(ws.icon ?? "");
  const [saving, setSaving] = useState(false);
  const isActive = activeId === ws.id;

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    await api.workspaces
      .save(syncKey, [{ id: ws.id, name: name.trim(), icon: icon.trim() || null }])
      .catch(() => {});
    setSaving(false);
    setEditing(false);
    onReload();
  }

  async function deleteWorkspace() {
    if (!confirm(`Delete workspace "${ws.name}"?`)) return;
    if (isActive) onSetActive(null);
    await api.workspaces
      .save(syncKey, [{ id: ws.id, name: ws.name, icon: ws.icon, deletedAt: Math.floor(Date.now() / 1000) }])
      .catch(() => {});
    onReload();
  }

  return (
    <div className="settings-section" style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 20 }}>{ws.icon ?? "📁"}</span>
        {editing ? (
          <>
            <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="emoji" style={{ width: 50 }} />
            <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} autoFocus style={{ flex: 1 }} />
            <button type="button" onClick={save} disabled={saving || !name.trim()}>{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={() => { setEditing(false); setName(ws.name); setIcon(ws.icon ?? ""); }}>Cancel</button>
          </>
        ) : (
          <>
            <span style={{ flex: 1, fontWeight: 600 }}>{ws.name}</span>
            <span style={{ fontSize: 11, color: "#666" }}>modified {fmtDate(ws.updatedAt)}</span>
            <button
              type="button"
              onClick={() => onSetActive(isActive ? null : ws.id)}
              style={{ fontSize: 11, padding: "2px 8px" }}
              className={isActive ? "" : ""}
            >
              {isActive ? "✓ Active" : "Set Active"}
            </button>
            <button type="button" onClick={() => setEditing(true)} style={{ fontSize: 11, padding: "2px 8px" }}>Edit</button>
            <button type="button" onClick={() => setExpanded((v) => !v)} style={{ fontSize: 11, padding: "2px 8px" }}>
              {expanded ? "Hide config" : "Config"}
            </button>
            <button type="button" onClick={deleteWorkspace} className="clip-icon-btn" title="Delete workspace" aria-label="Delete">🗑</button>
          </>
        )}
      </div>
      {expanded && (
        <ConfigEditor workspace={ws} configs={configs} syncKey={syncKey} onReload={onReload} />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Workspaces({ syncKey }: Props) {
  const [workspaces, setWorkspaces] = useState<ServerWorkspace[]>([]);
  const [configs, setConfigs] = useState<ServerWorkspaceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveIdState] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState("");
  const [creating, setCreating] = useState(false);

  // Active workspace stored as a sync item setting
  async function loadActiveId() {
    try {
      const res = await api.sync.settings(syncKey);
      const item = res.items.find((i) => i.key === "active_workspace_id");
      setActiveIdState(item ? (JSON.parse(item.value) as string | null) : null);
    } catch {
      setActiveIdState(null);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wsRes, cfgRes] = await Promise.all([
        api.workspaces.list(syncKey),
        api.workspaces.listConfigs(syncKey),
      ]);
      setWorkspaces(wsRes.workspaces.filter((w) => !w.deletedAt).sort((a, b) => b.updatedAt - a.updatedAt));
      setConfigs(cfgRes.configs);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [syncKey]);

  useEffect(() => {
    load();
    loadActiveId();
  }, [load]);

  async function setActive(id: string | null) {
    setActiveIdState(id);
    await api.sync.saveSettings(syncKey, [{ key: "active_workspace_id", value: JSON.stringify(id) }]).catch(() => {});
  }

  async function createWorkspace() {
    const n = newName.trim();
    if (!n) return;
    setCreating(true);
    const id = newId();
    await api.workspaces
      .save(syncKey, [{ id, name: n, icon: newIcon.trim() || null }])
      .catch(() => {});
    setNewName("");
    setNewIcon("");
    setCreating(false);
    load();
  }

  return (
    <div className="page">
      <h1>Workspaces</h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
        Workspaces let you switch between named project contexts with isolated config keys. Changes sync across all devices.
      </p>

      <div className="settings-section">
        <h2>Create workspace</h2>
        <div className="push-row">
          <input
            placeholder="emoji (optional)"
            value={newIcon}
            onChange={(e) => setNewIcon(e.target.value)}
            style={{ width: 80 }}
          />
          <input
            placeholder="Workspace name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createWorkspace(); }}
            style={{ flex: 1 }}
          />
          <button type="button" onClick={createWorkspace} disabled={creating || !newName.trim()}>
            {creating ? "Creating…" : "Create"}
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: "#666" }}>Loading…</p>
      ) : workspaces.length === 0 ? (
        <p className="empty">No workspaces yet. Create one above.</p>
      ) : (
        <div>
          <h2 style={{ marginBottom: 12 }}>Your workspaces ({workspaces.length})</h2>
          {workspaces.map((ws) => (
            <WorkspaceRow
              key={ws.id}
              ws={ws}
              configs={configs}
              syncKey={syncKey}
              onReload={load}
              activeId={activeId}
              onSetActive={setActive}
            />
          ))}
        </div>
      )}
    </div>
  );
}
